import React from "react";
import moment from "moment";
import { Link } from "react-router";
import { Table, Tr, Td, Sort } from "reactable";
import { RaisedButton } from "material-ui";

import StatsBlock from "./stats-block.jsx";
import TableRefreshIndicator from "./table-refresh-indicator.jsx";
import InlineLinkIconButton from "./inline-link-icon-button.jsx";

import UserStore from "../../stores/UserStore";
import ItemActions from "../../actions/ItemActions";
import OrderActions from "../../actions/OrderActions";

import { formatPrice } from "../../util";

const PAGE_SIZE = 20;

const DELETED_ROW_STYLE = { textDecoration: "line-through", color: "#ccc" };
const HIGHLIGHTED_ROW_STYLE = {
  backgroundColor: "#fff176",
  boxShadow: "1px 1px 10px rgba(0,0,0,0.2)"
};

class ItemTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      deletedItems: {},
      highlightedRow: null,
      currentPage: 0
    };

    this._makeRowForItem = this._makeRowForItem.bind(this);
    this._refresh = this._refresh.bind(this);
    this._viewDonationReceipt = this._viewDonationReceipt.bind(this);
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  _delete(id, e) {
    e.preventDefault();
    e.stopPropagation();

    if (
      window.confirm(
        "Are you sure you want to delete item #" +
          id +
          "? There is no going back."
      )
    ) {
      ItemActions.delete(id);
      this.state.deletedItems[id] = true;
      this.setState(this.state);
    }
  }

  _refresh(e) {
    e.preventDefault();
    e.stopPropagation();

    ItemActions.fetchAllForItemBucket(this.props.itemBucket);
  }

  _viewDonationReceipt(item) {
    OrderActions.downloadDonationReceipt(
      this.props.order || item.order.id || item.order
    );
  }

  _makeRowForItem(item) {
    let isDeleted = this.state.deletedItems[item.id],
      shouldHighlight = this.state.highlightedId === item.id;

    let noWrapTdStyle = { whiteSpace: "nowrap" };

    return (
      <Tr
        style={Object.assign(
          {},
          isDeleted && DELETED_ROW_STYLE,
          shouldHighlight && HIGHLIGHTED_ROW_STYLE
        )}
        key={item.id}
      >
        <Td column="Id">{item.id}</Td>
        <Td
          column="Person"
          value={
            item.person
              ? item.person.name || item.person.email
              : "Unknown Person"
          }
          style={noWrapTdStyle}
        >
          {item.person ? (
            <div>
              {item.person.name || item.person.email}
              <InlineLinkIconButton
                onTouchTap={() => {
                  this.context.router.push(
                    `/event_buckets/${this.props.eventBucket ||
                      item.event.event_bucket}/people/${item.person.id}`
                  );
                }}
                tooltip="View person"
              />
            </div>
          ) : (
            "Unknown Person"
          )}
        </Td>
        <Td column="Item Bucket" value={item.item_bucket.name}>
          <div>
            {item.item_bucket.name}
            <InlineLinkIconButton
              onTouchTap={() => {
                this.context.router.push(
                  `/event_buckets/${this.props.eventBucket ||
                    item.event_bucket}/item_buckets/${item.item_bucket.id}`
                );
              }}
              tooltip="View item bucket"
            />
          </div>
        </Td>
        <Td column="Variant" style={noWrapTdStyle}>
          {`${item.variant.name || ""} ${
            item.price ? formatPrice(item.price) : ""
          }`}
        </Td>
        <Td column="Order Date" value={item.created_at} style={noWrapTdStyle}>
          <div>
            {moment(item.created_at).format("lll")}
            <InlineLinkIconButton
              onTouchTap={() => {
                this.context.router.push(
                  `/event_buckets/${this.props.eventBucket ||
                    item.event.event_bucket}/orders/${this.props.order ||
                    item.order}`
                );
              }}
              tooltip="View order"
            />
          </div>
        </Td>
        <Td column="Actions" style={noWrapTdStyle}>
          {
            <div>
              <div
                ref={r => {
                  if (shouldHighlight) {
                    this.highlightedRow = r;
                  }
                }}
              />
              {item.item_bucket.code === "donation" && (
                <span>
                  <a
                    href="#"
                    onClick={this._viewDonationReceipt.bind(this, item)}
                  >
                    Donation receipt
                  </a>
                </span>
              )}
              {!isDeleted && UserStore.hasPermission("manage_orders") && (
                <span>
                  &nbsp;
                  <a href="#" onClick={this._delete.bind(this, item.id)}>
                    Delete
                  </a>
                </span>
              )}
            </div>
          }
        </Td>
      </Tr>
    );
  }

  componentWillReceiveProps(props) {
    if (
      props.location &&
      props.location.query.item &&
      this.table &&
      this.table.data.length
    ) {
      let rowLocation = this.table.data.findIndex(
          p => p.data["Id"].value === props.location.query.item
        ),
        neededPage = Math.floor(rowLocation / PAGE_SIZE);

      this.setState({
        highlightedId: props.location.query.item,
        currentPage: neededPage
      });
    }
  }

  componentDidUpdate() {
    if (
      this.highlightedRow &&
      this.state.highlightedRow !== this.highlightedRow
    ) {
      this.setState(
        {
          highlightedRow: this.highlightedRow
        },
        () => {
          setTimeout(() => {
            if (
              this.highlightedRow &&
              this.highlightedRow.scrollIntoViewIfNeeded
            ) {
              // chrome only
              this.highlightedRow.scrollIntoViewIfNeeded();
            } else if (this.highlightedRow) {
              this.highlightedRow.scrollIntoView();
            }
          }, 0);
        }
      );
    }
  }

  render() {
    let rows = this.props.items.map(this._makeRowForItem),
      itemCount = this.props.items.length;

    return (
      <div>
        {this.props.itemBucket ? (
          <TableRefreshIndicator
            isRefreshing={this.props.isFetching}
            onRefresh={this._refresh}
            style={{ marginTop: 15 }}
          />
        ) : null}
        <Table
          ref={r => {
            this.table = r;
          }}
          className="bordered striped responsive-table"
          sortable={[
            { column: "Order Date", sortFunction: Sort.Date },
            { column: "Item Bucket", sortFunction: Sort.CaseInsensitive },
            { column: "Variant", sortFunction: Sort.CaseInsensitive },
            { column: "Person", sortFunction: Sort.CaseInsensitive }
          ]}
          defaultSort={{ column: "Event Date", direction: "asc" }}
          columns={(this.props.showItemBucket ? ["Item Bucket"] : [])
            .concat(this.props.showPerson ? ["Person"] : [])
            .concat(["Variant"])
            .concat(["Order Date", "Actions"])}
          filterable={["Person", "Item Bucket", "Variant"]}
          filterPlaceholder="Type to filter..."
          itemsPerPage={itemCount > PAGE_SIZE ? PAGE_SIZE : undefined}
          currentPage={this.state.currentPage}
          onPageChange={p => this.setState({ currentPage: p })}
        >
          {rows}
        </Table>
        {rows.length === 0 && !this.props.hideNoItemsMessage ? (
          <div className="empty-table-message">No items yet.</div>
        ) : null}
      </div>
    );
  }
}

export default ItemTable;



// WEBPACK FOOTER //
// ./src/app/components/partials/item-table.jsx